export enum UserRole {
  USER = "user",
  ADMIN = "admin",
  SUPERADMIN = "superadmin"
}

export enum TransactionType {
  CRYPTO_PURCHASE = "crypto_purchase",
  CRYPTO_SALE = "crypto_sale",
  CRYPTO_DEPOSIT = "crypto_deposit",
  GIFTCARD_PURCHASE = "giftcard_purchase",
  GIFTCARD_SALE = "giftcard_sale",
  WITHDRAWAL = "withdrawal",
  TOPUP = "topup",
  VIRTUAL_ACCOUNT_TOPUP = "virtual_account_topup",
  CONVERSION = "conversion"
}

export enum FeeType {
  FIXED = "fixed",
  PERCENTAGE = "percentage"
}

export enum TransactionStatus {
  COMPLETED = "completed",
  PENDING = "pending",
  FAILED = "failed"
}

export enum NotificationTarget {
  USER = "user",
  ADMINS = "admins",
  ALL_USERS = "all_users"
}

export enum RecurrenceType {
  DAILY = "daily",
  WEEKLY = "weekly",
  MONTHLY = "monthly"
}

export enum TransactionAction {
  APPROVE = "approve",
  REJECT = "reject"
}

export enum UserStatus {
  VERIFIED = "verified",
  UNVERIFIED = "unverified",
  BLOCKED = "blocked"
}

export enum CardType {
  PHYSICAL = "physical",
  ECODE = "ecode",
  CARD_URL = "card url"
}

export enum NotificationStatus {
  PENDING = "pending",
  SENT = "sent",
  STOPPED = "stopped",
  ACTIVE = "active"
}

export enum DateRange {
  TODAY = "today",
  THIS_WEEK = "this_week",
  THIS_MONTH = "this_month"
}

export interface ApiResponse<T = unknown> {
  status: "success" | "error";
  message: string;
  data?: T;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface LoginRequest {
  emailAddress: string;
  password: string;
}

export interface LoginResponse {
  id: string;
  token?: string;
  isPinSetup: boolean;
  passwordShouldReset: boolean;
  passwordResetToken: string | null;
  fullName: string;
  emailAddress: string;
  username: string | null;
  role: UserRole;
}

export interface ChangePasswordRequest {
  passwordResetToken: string;
  newPassword: string;
  confirmPassword: string;
}

export interface SetupPinRequest {
  pin: string;
  confirmPin: string;
}

export interface VerifyPinRequest {
  pin: string;
}

export interface AddUserRequest {
  fullName: string;
  emailAddress: string;
  phoneNumber: string;
  role: UserRole;
}

export interface AdminMember {
  id: string;
  fullName: string;
  emailAddress: string;
  role: UserRole;
  lastLoginTime: string;
}

export interface User {
  id: string;
  fullName: string;
  username: string;
  email: string;
  phone: string;
  status: UserStatus;
  registrationDate: string;
  bvnNumber?: string | null,
  isBvnVerified: boolean,
  ninNumber?: string | null,
  isNinVerified: boolean,
  wallet: {
    ngn: string;
    usd: string;
  };
}

export interface UserSearchResult {
  id: string;
  email: string;
  username?: string | null;
}

export interface UserTransaction {
  transactionId: string;
  type: TransactionType;
  asset?: string | null;
  amount: string;
  status: TransactionStatus;
  date: string;
}

export interface SendMoneyRequest {
  amount: number;
  userIds?: string[] | null;
  note: string;
}

export interface Analytics {
  totalVolume: string;
  volumePercentageChange: string;
  totalWithdrawal: string;
  withdrawalPercentageChange: string;
  pendingGiftcardSales: number;
  pendingKyc: number;
  totalUsers: number;
  usersPercentageChange: string;
  totalNairaBalance: string;
  totalUsdBalance: string;
  totalCryptoAssetValue: string;
}

export interface Transaction {
  id: string;
  username: string;
  type: TransactionType;
  title: string;
  amount: string;
  fee?: string | null;
  balanceBefore: string;
  balanceAfter: string;
  transactionId: string;
  reference: string | null;
  status: TransactionStatus;
  date: string;
  giftcardPurchase?: GiftcardPurchaseDetails | null;
  giftcardSale?: GiftcardSaleDetails | null;
  cryptoPurchase?: CryptoPurchaseDetails | null;
  cryptoSale?: CryptoSaleDetails | null;
  cryptoDeposit?: CryptoDepositDetails | null;
  topup?: TopupDetails | null;
  withdrawal?: WithdrawalDetails | null;
}

export interface GiftcardPurchaseDetails {
  brandName: string;
  productName: string;
  productId: string;
  ratePerDollar: string;
  cardNumber: string;
  pinCode: string;
  logoUrl: string;
}

export interface GiftcardSaleDetails {
  type: string;
  country: string;
  minAmount: string;
  maxAmount: string;
  cardUrls?: string[] | null;
  ecode?: string | null;
  ratePerDollar: string;
  rejectionReason?: string | null;
  subcategoryName?: string | null;
  logoUrl: string;
}

export interface CryptoPurchaseDetails {
  id?: string | null;
  currency: string;
  amountInCrypto?: string | null;
  ratePerDollar: string;
  logoUrl: string;
}

export interface CryptoSaleDetails {
  currency: string;
  amountInCrypto: string;
  ratePerDollar: string;
  logoUrl: string;
}

export interface CryptoDepositDetails {
  currency: string;
  amountInCrypto: string;
  ratePerDollar: string;
  logoUrl: string;
}

export interface TopupDetails {
  accountNumber: string;
  accountName: string;
}

export interface WithdrawalDetails {
  accountNumber: string;
  accountName: string;
  bankCode: string;
  bankName: string;
}

export interface PendingGiftcardSale {
  id: string;
  transactionId: string;
  type: TransactionType;
  username: string;
  emailAddress: string;
  cardType: CardType;
  giftcardType: string;
  amountInUsd: string;
  rate: string;
  status: TransactionStatus;
  date: string;
  cardImages?: string[] | null;
  amountInNaira: string;
  subcategory: string;
  ecode?: string | null;
}

export interface ApproveRejectGiftcardRequest {
  transactionId: string;
  action: TransactionAction;
  rejectionReason?: string;
}

export interface GiftcardAsset {
  type: string;
  logoUrl: string;
  isEnabled: boolean;
  regions?: GiftcardRegion[];
}

export interface GiftcardRegion {
  countryName: string;
  countryCode: string;
}

export interface GiftcardBuyPercentage {
  buyPercentage: string;
}

export interface UpdateGiftcardBuyPercentageRequest {
  buyPercentage: number;
}

export interface GiftcardBuyRate {
  type: string;
  logoUrl: string;
  regions: GiftcardBuyRegion[];
}

export interface GiftcardBuyRegion {
  countryName: string;
  countryCode: string;
  flagUrl: string;
  products: GiftcardBuyProduct[];
}

export interface GiftcardBuyProduct {
  productId: number;
  productName: string;
  amountsInUSD: number[];
  buyPrices: number[];
}

export interface GiftcardSellRate {
  id: string;
  type: string;
  logoUrl: string;
  regions: GiftcardSellRegion[];
}

export interface GiftcardSellRegion {
  id: string;
  countryName: string;
  countryCode: string;
  flagUrl: string;
  subcategories: GiftcardSubcategory[];
}

export interface GiftcardSubcategory {
  id: string;
  name: string;
  minAmount: string;
  maxAmount: string;
  sellRate: string;
}

export interface UpdateGiftcardRegionsRequest {
  countryCodes: string[];
}

export interface CreateGiftcardSubcategoryRequest {
  name: string;
  minAmount: number;
  maxAmount: number;
  sellRate: number;
}

export interface UpdateGiftcardSubcategoryRequest {
  name?: string;
  minAmount?: number;
  maxAmount?: number;
  sellRate?: number;
}

export interface CryptoRate {
  name: string;
  currency: string;
  logoUrl: string;
  sellRateFromQuidax: string;
  buyRateFromQuidax: string;
  sellRate: string;
  buyRate: string;
}

export interface UpdateCryptoRateRequest {
  currency: string;
  rate: number;
}

export interface Country {
  name: string;
  code: string;
}

export interface ConversionRate {
  rate: string;
}

export interface UpdateConversionRateRequest {
  rate: number;
}

export interface SendNotificationRequest {
  title: string;
  body: string;
  targetType: NotificationTarget;
  userId?: string;
  scheduledAt?: string;
  isRecurring?: boolean;
  recurrenceType?: RecurrenceType;
  dayOfWeek?: number;
  dayOfMonth?: number;
  timeOfDay?: string;
}

export interface Notification {
  id: string;
  title: string;
  body: string;
  status: NotificationStatus;
  scheduledAt: string | null;
  isRecurring: boolean;
  recurrenceType: RecurrenceType | null;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  timeOfDay: string | null;
  lastSentAt: string | null;
  userId: string | null;
}

export interface UpdateNotificationRequest {
  title?: string;
  body?: string;
  scheduledAt?: string;
  isRecurring?: boolean;
  recurrenceType?: RecurrenceType;
  dayOfWeek?: number;
  dayOfMonth?: number;
  timeOfDay?: string;
}

export interface PaginationParams {
  pageNumber?: number;
  pageSize?: number;
  [key: string]: unknown;
}

export interface TransactionFilters extends PaginationParams {
  search?: string;
  type?: TransactionType;
  status?: TransactionStatus;
  dateRange?: DateRange;
}

export interface NotificationFilters {
  status?: NotificationStatus;
  search?: string;
  [key: string]: unknown;
}

export interface UserSearchParams {
  search: string;
  [key: string]: unknown;
}

export interface TransactionFee {
  id: string;
  transactionType: TransactionType;
  minAmount: string;
  maxAmount: string;
  feeType: FeeType;
  feeValue: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTransactionFeeRequest {
  transactionType: TransactionType;
  minAmount: number;
  maxAmount: number;
  feeType: FeeType;
  feeValue: number;
}

export interface UpdateTransactionFeeRequest {
  transactionType?: TransactionType;
  minAmount?: number;
  maxAmount?: number;
  feeType?: FeeType;
  feeValue?: number;
}

export interface ActivityLog {
  id: string;
  adminName: string;
  role: string;
  description: string;
  date: string;
}

export interface ActivityLogsResponse {
  logs: ActivityLog[];
  total: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface ActivityLogFilters extends PaginationParams {
  search?: string;
}

export interface WithdrawalLimit {
  id: string;
  dailyLimit: string;
  weeklyLimit: string;
  monthlyLimit: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWithdrawalLimitRequest {
  dailyLimit: number;
  weeklyLimit: number;
  monthlyLimit: number;
}

export interface UpdateWithdrawalLimitRequest {
  dailyLimit: number;
  weeklyLimit: number;
  monthlyLimit: number;
}